import { styled, alpha } from '@mui/material/styles'
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  IconButton,
  Chip,
  Dialog,
  Button
} from '@mui/material'
/** Workspace theme via `applySevvCssVariables` on :root; literals are fallbacks only. */
const C = {
  primary: 'var(--sevv-primary, #f97415)',
  primaryLight: 'var(--sevv-primary-light, #ffa047)',
  primaryPale: 'var(--sevv-primary-pale, #fff2d6)',
  paper: 'var(--sevv-bg-paper, #ffffff)',
  shellSidebar: 'var(--sevv-shell-sidebar-bg, var(--sevv-bg-paper, #ffffff))',
  bgDefault: 'var(--sevv-bg-default, #fafafa)',
  text: 'var(--sevv-text-primary, #424242)',
  textSec: 'var(--sevv-text-secondary, #757575)',
  divider: 'var(--sevv-divider, #eeeeee)',
  g100: 'var(--sevv-gray-100, #f5f5f5)',
  g300: 'var(--sevv-gray-200, #e0e0e0)',
  g400: 'var(--sevv-gray-400, #bdbdbd)',
  g500: 'var(--sevv-gray-600, #9e9e9e)',
  g700: 'var(--sevv-gray-800, #616161)',
  onPrimary: 'var(--sevv-white, #ffffff)',
  shSm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  shMd: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  shLg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  sh2xl: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  shPri: 'var(--sevv-shadow-primary, 0 4px 14px 0 rgba(249, 116, 21, 0.3))',
  shPriLg: 'var(--sevv-shadow-primary-lg, 0 10px 25px 0 rgba(249, 116, 21, 0.4))',
  rMd: '8px',
  rLg: '12px',
  rXl: '16px',
  ff: `var(--sevv-font-family, 'Inter', 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif)`,
} as const

export const MenuContainer = styled(Paper)(({ theme }) => ({
  borderRadius: 0,
  overflow: 'hidden',
  width: '100%',
  maxWidth: 'none',
  display: 'flex',
  flex: 1,
  minHeight: 0,
  zIndex: 1051,
  boxShadow: 'none',
  background: 'transparent',
  height: 'auto',
  maxHeight: 'none',
  fontFamily: C.ff,
  alignItems: 'stretch',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
  },
}))

export const Backdrop = styled(Box)(() => ({
  position: 'fixed',
  inset: 0,
  width: '100%',
  minHeight: '100dvh',
  height: '100%',
  background: C.bgDefault,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  justifyContent: 'stretch',
  zIndex: 1050,
  overflow: 'hidden',
}))

export const Sidebar = styled(Box)(({ theme }) => ({
  width: 280,
  height: '100%',
  minHeight: 0,
  background: C.shellSidebar,
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  borderRight: `1px solid ${C.divider}`,
  boxShadow: theme.shadows[1],
  fontFamily: C.ff,
}))

export const ContentPanel = styled(Box)(({ theme }) => `
  flex: 1;
  min-height: 0;
  background: ${C.bgDefault};
  padding: 24px;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  position: relative;
  color: ${theme.palette.text.primary};
  font-family: ${C.ff};

  ${theme.breakpoints.up('md')} {
    border-left: 1px solid ${C.divider};
  }

  ${theme.breakpoints.down('sm')} {
    padding: 16px;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 100%;
    height: 100%;
    background: radial-gradient(circle at top right, color-mix(in srgb, ${C.primary} 4%, transparent), transparent 60%);
    z-index: -1;
  }

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: ${C.g100};
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${C.g300};
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: ${C.g400};
  }
  -ms-overflow-style: none;
  scrollbar-width: thin;
`)

export const LogoContainer = styled(Box)`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
  padding: 20px 16px 16px;
  margin-bottom: 8px;
  border-bottom: 1px solid ${C.divider};
  background: ${C.paper};

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, ${C.primary} 0%, ${C.primaryLight} 100%);
  }

  .logo-badge {
    width: 56px;
    height: 56px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${C.paper};
    border: 1px solid color-mix(in srgb, ${C.primary} 25%, transparent);
    box-shadow: ${C.shPri};
    margin-bottom: 10px;
  }

  .brand-name {
    font-weight: 700;
    color: ${C.text};
    letter-spacing: 0.02em;
    font-size: 1rem;
  }

  .brand-subtext {
    color: ${C.textSec};
    font-size: 0.75rem;
    font-weight: 500;
  }
`

export const MenuList = styled(List)`
  padding: 0;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: ${C.g100};
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${C.g300};
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: ${C.g400};
  }
  scrollbar-width: thin;
`

export const MenuListItem = styled(ListItem, {
  shouldForwardProp: (prop) => prop !== '$active',
})<{ $active: boolean }>(({ theme, $active }) => {
  const isDark = theme.palette.mode === 'dark'
  const primary = theme.palette.primary.main
  const activeBg = isDark
    ? alpha(primary, 0.26)
    : C.primaryPale
  const hoverActiveBg = isDark
    ? alpha(primary, 0.34)
    : 'color-mix(in srgb, var(--sevv-primary, #f97415) 12%, transparent)'
  const hoverInactiveBg = isDark
    ? alpha(primary, 0.1)
    : 'color-mix(in srgb, var(--sevv-primary, #f97415) 6%, transparent)'
  const activeIconColor = isDark ? theme.palette.primary.light : primary
  const activeTextColor = theme.palette.text.primary
  const inactiveIconColor = theme.palette.text.secondary
  const inactiveTextColor = theme.palette.text.secondary

  return {
    padding: '12px 16px',
    minHeight: 44,
    alignItems: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    position: 'relative',
    marginBottom: '4px',
    borderRadius: `0 ${C.rMd} ${C.rMd} 0`,
    marginRight: '8px',
    background: $active ? activeBg : 'transparent',
    ...($active && {
      '&::before': {
        content: '""',
        position: 'absolute',
        left: 0,
        top: 0,
        height: '100%',
        width: 4,
        background: primary,
        borderRadius: '0 2px 2px 0',
      },
    }),
    '&:hover': {
      background: $active ? hoverActiveBg : hoverInactiveBg,
    },
    '&:focus-visible': {
      outline: `2px solid ${primary}`,
      outlineOffset: 2,
    },
    '& .MuiListItemIcon-root': {
      color: $active ? activeIconColor : inactiveIconColor,
      minWidth: 40,
    },
    '& .MuiListItemText-root': {
      flex: 1,
      minWidth: 0,
    },
    '& .MuiListItemText-primary': {
      color: $active ? activeTextColor : inactiveTextColor,
      fontWeight: $active ? 600 : 400,
      fontSize: '0.9375rem',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  }
})

export const UserProfile = styled(Box)`
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
  border-top: 1px solid ${C.divider};
  background: ${C.bgDefault};
  border-radius: 0 0 ${C.rXl} 0;
`

export const UserInfo = styled(Box)`
  flex: 1;
  overflow: hidden;
`

export const UserName = styled(Typography)`
  font-weight: 600;
  color: ${C.text};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 0.9375rem;
  letter-spacing: 0.02em;
`

export const UserRole = styled(Typography)`
  font-size: 0.8rem;
  color: ${C.textSec};
  font-weight: 500;
  text-transform: capitalize;
  letter-spacing: 0.01em;
`

export const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.1rem',
  fontWeight: 600,
  color: theme.palette.primary.main,
}))

export const RoomGrid = styled(Box)`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 16px;
  margin-bottom: 32px;
`

export const RoomCardContainer = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  borderRadius: C.rMd,
  border: `1px solid ${theme.palette.divider}`,
  overflow: 'hidden',
  transition: 'all 0.2s ease',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  boxShadow: theme.shadows[1],
  backgroundImage: 'none',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[4],
    borderColor: alpha(theme.palette.primary.main, 0.35),
  },
}))

export const ActionButton = styled(Button)`
  min-height: 44px;
  border-radius: ${C.rMd};
  text-transform: none;
  font-size: 0.875rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  box-shadow: ${C.shPri};
  background: linear-gradient(135deg, ${C.primary} 0%, ${C.primaryLight} 100%);
  position: relative;
  overflow: hidden;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  color: ${C.onPrimary} !important;

  &:hover {
    box-shadow: ${C.shPriLg};
    transform: translateY(-1px);
    background: linear-gradient(135deg, ${C.primary} 0%, ${C.primaryLight} 100%);
    filter: brightness(0.96);
    color: ${C.onPrimary} !important;
  }

  &:active {
    transform: translateY(0);
    box-shadow: ${C.shPri};
    color: ${C.onPrimary} !important;
  }

  &.loading {
    opacity: 0.7;
    cursor: default;
  }

  .icon {
    color: ${C.onPrimary};
  }
`

export const LoadingContainer = styled(Box)(({ theme }) => `
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
`)

export const NoRoomsMessage = styled(Typography)(({ theme }) => `
  text-align: center;
  color: ${C.textSec};
  margin-top: 24px;
  font-size: 0.9375rem;
`)

export const PasswordDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiPaper-root': {
    background: C.paper,
    backgroundImage: 'none',
    borderRadius: C.rXl,
    border: `1px solid ${C.divider}`,
    color: C.text,
    overflow: 'hidden',
    boxShadow: theme.shadows[24],
  },
  '& .MuiDialogTitle-root': {
    background: C.bgDefault,
    backgroundImage: 'none',
    color: C.text,
    fontWeight: 600,
    borderBottom: `1px solid ${C.divider}`,
  },
  '& .MuiDialogContent-root': {
    color: C.g700,
  },
}))

export const ContentHeader = styled(Box)(({ theme }) => `
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`)

export const PageTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.5rem',
  fontWeight: 600,
  color: theme.palette.text.primary,
  letterSpacing: '-0.01em',
}))

export const RoomsScrollContainer = styled(Box)(({ theme }) => `
  display: flex;
  overflow-x: auto;
  gap: 16px;
  margin-top: 16px;
  padding-bottom: 16px;
  scroll-behavior: smooth;
  scroll-padding: 16px;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    height: 6px;
  }

  &::-webkit-scrollbar-track {
    background: ${C.g100};
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${C.g300};
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: color-mix(in srgb, var(--sevv-primary, #f97415) 40%, transparent);
  }

  @media (max-width: 600px) {
    scroll-snap-type: x mandatory;
    & > * {
      scroll-snap-align: start;
    }
  }
`)

export const RoomCardWrapper = styled(Box)(({ theme }) => `
  min-width: 280px;
  max-width: 280px;
`)

export const SectionDivider = styled(Box)(({ theme }) => `
  display: flex;
  align-items: center;
  margin: 32px 0 16px;

  &:before {
    content: '';
    flex: 1;
    border-bottom: 1px solid ${C.divider};
    margin-right: 16px;
  }
`)

export const NoRooms = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: 40,
  background: C.paper,
  backgroundImage: 'none',
  borderRadius: C.rLg,
  border: `1px solid ${C.divider}`,
  marginTop: 24,
  boxShadow: theme.shadows[1],
}))

export const EmptyStateIcon = styled(Box)(({ theme }) => `
  font-size: 48px;
  color: ${C.g400};
  margin-bottom: 16px;
`)

export const ProfileContainer = styled(Box)(({ theme }) => `
  display: flex;
  flex-direction: column;
  gap: 24px;
`)

export const ProfileSection = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  borderRadius: C.rLg,
  padding: 24,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: theme.shadows[1],
  backgroundImage: 'none',
}))

export const ProfileHeader = styled(Box)(({ theme }) => `
  display: flex;
  align-items: center;
  gap: 24px;
  margin-bottom: 24px;

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: center;
  }
`)

export const LargeAvatar = styled(Box)(({ theme }) => `
  width: 100px;
  height: 100px;
  border: 3px solid ${C.primary};
  box-shadow: ${C.shPri};
  border-radius: 50%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
`)

export const ProfileDetails = styled(Box)(({ theme }) => `
  flex: 1;
`)

export const ProfileSectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1rem',
  fontWeight: 600,
  color: theme.palette.primary.main,
  marginBottom: '16px',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
}))

export const ProfileField = styled(Box)(({ theme }) => `
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }
`)

export const FieldLabel = styled(Typography)(({ theme }) => `
  font-size: 0.85rem;
  color: ${C.textSec};
  margin-bottom: 4px;
  font-weight: 500;
`)

export const FieldValue = styled(Typography)(({ theme }) => `
  font-size: 1rem;
  color: ${C.text};
  font-weight: 500;
`)

export const AdminMenuContainer = styled(Box)(({ theme }) => `
  padding: ${theme.spacing?.(3) || '24px'};
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: ${theme.spacing?.(3) || '24px'};
`)

export const AdminMenuCard = styled(Paper)(({ theme }) => ({
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  borderRadius: C.rLg,
  border: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  padding: theme.spacing(3),
  boxShadow: theme.shadows[1],
  backgroundImage: 'none',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[4],
    borderColor: alpha(theme.palette.primary.main, 0.35),
  },
}))

export const CardIconWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background:
    theme.palette.mode === 'dark'
      ? alpha(theme.palette.primary.main, 0.22)
      : C.primaryPale,
  color: theme.palette.primary.main,
  borderRadius: C.rLg,
  width: 56,
  height: 56,
  marginRight: 16,
  transition: 'all 0.2s ease',
}))

export const AdminRoomCard = styled(Paper)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  transition: 'all 0.2s ease',
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  borderRadius: C.rLg,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: theme.shadows[1],
  backgroundImage: 'none',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[4],
  },
}))

export const AdminRoomCardContent = styled(Box)(({ theme }) => `
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex-grow: 1;
  padding: 24px;
`) 